import type { TokenUsage } from '@/types/msq'

/**
 * Agent Pages — shared types.
 *
 * Copied verbatim from the pinned implementation contracts
 * (docs/agent-pages-contracts.md §1 layout DSL, §5 record types, §9 PageDraftInput,
 * §11 HTTP response shapes, §15 X402NetworkInfo). The repos are not monorepo-linked,
 * so these mirror missionsquad-api/src/types/pages.ts exactly.
 */

// ---------------------------------------------------------------------------
// §1 Layout DSL (stored verbatim; identical types in all repos)
// ---------------------------------------------------------------------------

export type PageLayoutFieldType = 'richtext' | 'text' | 'number' | 'list' | 'url' | 'enum' | 'image'
export type PageView = 'daily' | 'weekly' | 'monthly' | 'ondemand'

export interface PageLayoutItemField {
  name: string // ^[a-z][a-z0-9_]*$ , 1–40 chars, unique within the item
  type: 'text' | 'number' | 'url' | 'enum' | 'image' // one nesting level: no list/richtext inside items
  description?: string // ≤ 500 chars
  options?: string[] // enum only (required for enum): 1–20 options, each ≤ 80 chars
}

export interface PageLayoutField {
  name: string // ^[a-z][a-z0-9_]*$ , 1–40 chars, unique among top-level fields
  type: PageLayoutFieldType
  description?: string // ≤ 500 chars — sent to the agent as the JSON Schema description
  display?: string // presentation hint; validated server-side; never in the compiled schema
  maxItems?: number // list only, 1–50
  options?: string[] // enum only (required for enum): 1–20, each ≤ 80 chars
  item?: { fields: PageLayoutItemField[] } // list only (required for list): 1–8 item fields
}

export interface PageLayout {
  title: string // 1–120 chars
  layout: 'report' // only template at launch
  fields: PageLayoutField[] // 1–24 fields
}

// ---------------------------------------------------------------------------
// §5 Record types
// ---------------------------------------------------------------------------

export interface PageHeader {
  title: string
  description: string
  ownerDisplay: string
  sourceDisplay?: string // optional owner-set public byline ("Econ Desk workflow")
}

export interface PageScheduleConfig {
  cadence: 'daily' | 'weekly'
  dayOfWeek?: number // 0 (Sunday) – 6, required when cadence='weekly'
  hour: number // 0–23, wall clock in `timezone`
  minute: number // 0–59
  timezone: string // IANA, validated via Intl
  periodCaption?: string // optional interval-toggle caption override (IPO Radar)
  aggregation: { enabled: boolean; views: PageView[] } // subset of daily|weekly|monthly; must include the cadence view
}

export interface PageInputField {
  name: string // snake_case key, unique
  label: string
  type: 'text' | 'select'
  required: boolean
  placeholder?: string
  validation?: string // anchored regex source for type=text
  options?: string[] // for type=select (required for select)
}

export interface PageOnDemandConfig {
  storeHistory: boolean
  inputForm: PageInputField[] // 0–12 fields
  freeRunDailyCap?: number // 1..PAGES_FREE_RUN_DAILY_CAP_MAX; default = PAGES_FREE_RUN_DAILY_CAP_DEFAULT
}

export interface PagePaymentConfig {
  enabled: boolean
  network: string // CAIP-2
  payTo: string
  priceUsd: string
}

export interface PagePlatformOptions {
  publicListed: boolean
  subscribe: { enabled: boolean; frequencies: Array<'daily' | 'weekly' | 'monthly'> }
  emailReady: boolean
}

export interface PageScheduleState {
  nextRunAt?: number
  consecutiveFailures: number
  pausedAt?: number
}

export type PageSourceType = 'agent' | 'workflow' | 'factory'
export type PageType = 'user' | 'platform'
export type PageStatus = 'draft' | 'live' | 'unpublished'
export type PageRunMode = 'scheduled' | 'on-demand'

export interface PageRecord {
  id: string // uuid v4 — never derived from title
  userId: string // owner username
  slug?: string // allocated at first publish; GLOBALLY unique; stable across renames
  previousSlugs?: string[]
  pageType: PageType
  status: PageStatus
  /** User pages only: public-directory listing; absent = listed (opt-out flag). */
  publicListed?: boolean
  /**
   * User pages only. 'private' = served ONLY to the owner on every public
   * /p/<slug> surface (everyone else gets the unknown-slug 404) and excluded
   * from listing feeds. Absent = 'public'. Mutually exclusive with
   * payment.enabled.
   */
  visibility?: 'public' | 'private'
  /**
   * §26 directory-filter labels, normalized server-side (lowercase [a-z0-9 ],
   * 2–24 chars, ≤5, profanity-screened). Absent = uncategorized.
   */
  categories?: string[]
  header: PageHeader
  source: { type: PageSourceType; id: string }
  layout: PageLayout
  runMode: PageRunMode
  schedule?: PageScheduleConfig
  scheduleState?: PageScheduleState
  onDemand?: PageOnDemandConfig
  payment?: PagePaymentConfig
  platformOptions?: PagePlatformOptions
  createdAt: number
  updatedAt: number
  publishedAt?: number
}

export interface PageRunRecord {
  runId: string // uuid v4
  pageId: string
  userId: string
  view: PageView
  periodKey?: string // daily 'YYYY-MM-DD' (page tz) · weekly ISO 'YYYY-Www' · monthly 'YYYY-MM'
  input?: Record<string, string>
  inputSummary?: string // values joined ' · ', truncated to 120 chars
  content?: unknown
  status: 'queued' | 'running' | 'completed' | 'error'
  error?: string
  usage?: TokenUsage
  paymentId?: string
  priceUsd?: string
  source?: 'x402-paid' | 'owner' | 'free'
  startedAt: number
  completedAt?: number
}

export interface PageSummary {
  id: string
  slug?: string
  title: string
  status: PageRecord['status']
  pageType: PageRecord['pageType']
  runMode: PageRecord['runMode']
  updatedAt: number
  publishedAt?: number
  lastRunAt?: number
  lastRunStatus?: PageRunRecord['status']
  schedulePaused?: boolean
  /** Present ONLY when the page is private (owner-only serving). */
  visibility?: 'private'
}

// ---------------------------------------------------------------------------
// §9 PageDraftInput — everything user-editable
// ---------------------------------------------------------------------------

export interface PageDraftInput {
  pageType: PageRecord['pageType']
  header: PageHeader
  source: PageRecord['source']
  layout: PageLayout
  runMode: PageRecord['runMode']
  schedule?: PageScheduleConfig
  onDemand?: PageOnDemandConfig
  payment?: PagePaymentConfig
  platformOptions?: PagePlatformOptions
  publicListed?: boolean // user pages only: public-directory listing opt-out (absent = listed)
  visibility?: PageRecord['visibility'] // user pages only: 'private' = owner-only serving (absent = 'public')
  categories?: string[] // raw labels — normalized server-side per §26 before persisting
  slug?: string // explicit slug change (live pages only)
}

// ---------------------------------------------------------------------------
// §15 x402 networks + §11 response wrappers for the routes the builder calls
// ---------------------------------------------------------------------------

export interface X402NetworkInfo {
  network: string // CAIP-2, e.g. 'eip155:8453'
  displayName: string
  available: boolean
  testnet: boolean
}

export interface PageListResponse {
  pages: PageSummary[]
  /** Environment's public origin for `/p/<slug>` links (PAGES_PUBLIC_ORIGIN on the API). */
  publicOrigin?: string
}

export interface PageResponse {
  page: PageRecord
  /** Environment's public origin for `/p/<slug>` links (PAGES_PUBLIC_ORIGIN on the API). */
  publicOrigin?: string
}

export interface PageDeleteResponse {
  ok: true
}

export interface PagePreviewRunResponse {
  runId: string
}

export interface PageRunsResponse {
  runs: PageRunRecord[]
  total: number
}

export interface PagePreviewTokenResponse {
  token: string
  path: string // '/p/preview/<id>'
}

export interface X402NetworksResponse {
  networks: X402NetworkInfo[]
}

/** GET /v1/core/pages/categories — §26 auto-expanding suggestion list (seeds ∪ in use). */
export interface PageCategoriesResponse {
  categories: string[]
}

/** POST /v1/core/pages/layout-schema — compiled JSON Schema for a layout. */
export interface PageLayoutSchemaResponse {
  schema: Record<string, unknown>
}

/**
 * One settled x402 payment (GET /v1/core/x402/settlements) — mirrors
 * missionsquad-api src/services/paymentSettlement.ts. `amount` is ATOMIC asset
 * units (USDC has 6 decimals: '20000' = 0.02). Page settlements carry
 * `agentId: null`; the page id lives inside `resourceId`
 * (`paid-page-<len>-<userId>-<pageId>`), parsed with paidPageIdFromResourceId.
 */
export interface PaymentSettlement {
  paymentId: string
  resourceId: string
  userId: string | null
  agentId: string | null
  kind: string
  transaction: string
  network: string
  payer: string | null
  scheme: string
  amount: string
  asset: string
  payTo: string
  settledAt: number
  recordedAt: number
}

export interface SettlementAssetSummary {
  network: string
  asset: string
  count: number
  totalAmount: string
}

export interface X402SettlementsResponse {
  settlements: PaymentSettlement[]
  summary: { count: number; byAsset: SettlementAssetSummary[] }
}

const PAID_PAGE_RESOURCE_PREFIX = 'paid-page-'

/**
 * Extracts the pageId from a paid-page x402 resource id
 * (`paid-page-<userId.length>-<userId>-<pageId>`, per the API's
 * pageX402ResourceStore), or null for agent/foreign resource ids. The length
 * prefix makes the parse unambiguous even when userId contains '-'.
 */
export function paidPageIdFromResourceId(resourceId: string): string | null {
  if (!resourceId.startsWith(PAID_PAGE_RESOURCE_PREFIX)) return null
  const rest = resourceId.slice(PAID_PAGE_RESOURCE_PREFIX.length)
  const lengthEnd = rest.indexOf('-')
  if (lengthEnd <= 0) return null
  const userIdLength = Number(rest.slice(0, lengthEnd))
  if (!Number.isInteger(userIdLength) || userIdLength <= 0) return null
  const pageIdStart = lengthEnd + 1 + userIdLength + 1
  if (rest.length <= pageIdStart || rest.charAt(lengthEnd + 1 + userIdLength) !== '-') return null
  return rest.slice(pageIdStart)
}

/**
 * Production fallback for the public host that serves `/p/<slug>` (the Astro
 * marketing origin — NOT the SPA origin). Used only when the API has not
 * supplied an environment-specific `publicOrigin` (PAGES_PUBLIC_ORIGIN /
 * FRONTEND_URL); every pages response carries one, so this is a cold-start
 * fallback, not the source of truth.
 */
export const DEFAULT_PUBLIC_PAGE_ORIGIN = 'https://missionsquad.ai'

/** The effective public-pages origin: API-provided when available, prod fallback otherwise. */
export function resolvePublicPageOrigin(publicOrigin?: string): string {
  const trimmed = (publicOrigin ?? '').trim().replace(/\/+$/, '')
  return trimmed !== '' ? trimmed : DEFAULT_PUBLIC_PAGE_ORIGIN
}

/** Absolute public URL for a published page slug, on the environment's origin. */
export function publicPageUrl(slug: string, publicOrigin?: string): string {
  return `${resolvePublicPageOrigin(publicOrigin)}/p/${slug}`
}

/** Chip/label text for a published page URL: the origin without its scheme + /p/<slug>. */
export function publicPageDisplay(slug: string, publicOrigin?: string): string {
  return publicPageUrl(slug, publicOrigin).replace(/^https?:\/\//, '')
}
